/* eslint-disable no-nested-ternary */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect, Link } from 'react-router-dom';
import { FormattedMessage, FormattedHTMLMessage, injectIntl, intlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { format } from '../../util/fiFormatter';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { isCommonPasswordValidationError } from '../../util/passwordPolicy';
import {
  AuthenticationPageReview,
  Page,
  NamedLink,
  NamedRedirect,
  LinkTabNavHorizontal,
  IconEmailSent,
  InlineTextButton,
  IconClose,
  Logo,
  LayoutWrapperMain,
  FacebookLoginButton,
  GoogleLoginButton,
  Modal,
  TermsOfService,
} from '../../components';
import { LoginForm, SignupForm, IdpSignupForm, OtpForm } from '../../forms';
import {
  login,
  authenticationInProgress,
  signup,
  signupStarted,
  signupWithIdp,
} from '../../ducks/Auth.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import affiliate from '../../util/affiliate';

import css from './AuthenticationPage.module.css';

// Same rule as server/api/auth/idpRedirectHelpers normalizeRedirectPath.
// OTP follows ?from= from the new UI, so only an in-app path is allowed.
const safeReturnPath = value => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\')) {
    return null;
  }
  try {
    const parsed = new URL(trimmed, 'https://landtrust.example');
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (error) {
    return null;
  }
};

const { loginPageReviews } = config.custom;

// OTP steps addressable by ?otp=. 'profile' is only a marker for the name
// step: after a reload it opens the email step, as the token is gone.
const OTP_URL_STEPS = ['email', 'code', 'profile'];
const otpStepFromLocation = location => {
  const step = queryString.parse(get(location, 'search', '') || '').otp;
  return OTP_URL_STEPS.includes(step) ? step : null;
};

export class AuthenticationPageComponent extends Component {
  constructor(props) {
    super(props);

    const { reviewIndex } = props;
    const currentMinute = new Date().getMinutes();

    this.state = {
      tosModalOpen: false,
      showPassword: false,
      isFromSignup: props.tab === 'signup',
      // eslint-disable-next-line react/no-unused-state
      authError: Cookies.get('st-autherror')
        ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
        : null,
      authInfo: Cookies.get('st-authinfo')
        ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
        : null,
      showTabs: true,
      // A deep link or refresh on ?otp= opens the email step (see OtpForm).
      otpStep: otpStepFromLocation(props.location) ? 'email' : 'start',
      review: loginPageReviews[reviewIndex || currentMinute % loginPageReviews.length],
    };

    this.toggleShow = this.toggleShow.bind(this);
    this.handleOtpStepChange = this.handleOtpStepChange.bind(this);

    const { signupStart, location } = this.props;
    if (props.tab === 'signup') {
      signupStart({ path: location.pathname });
    }
  }

  componentDidMount() {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    Cookies.remove('st-autherror');
  }

  componentDidUpdate(prevProps) {
    // The name step can't be left with browser Back (the code is spent, and
    // going back would desync the URL from the form): put the marker back.
    if (
      this.state.otpStep === 'profile' &&
      prevProps.location !== this.props.location &&
      otpStepFromLocation(this.props.location) !== 'profile'
    ) {
      this.pushOtpStep('profile');
    }
  }

  // Keep the URL in line with the step OtpForm shows: e.g. a deep link to the
  // code step without an email lands on the email step, and the name step
  // gets ?otp=profile in place of ?otp=code.
  handleOtpStepChange(step) {
    this.setState({ otpStep: step });
    const urlStep = otpStepFromLocation(this.props.location);
    if ((step === 'start' ? null : step) !== urlStep) {
      this.replaceOtpStep(step);
    }
  }

  // OTP steps live in ?otp=email|code so browser Back moves between them.
  // Other query params (e.g. from=) and location.state are kept.
  otpLocation(step, depth) {
    const { location } = this.props;
    const query = queryString.parse(get(location, 'search', ''));
    if (step === 'start') {
      delete query.otp;
    } else {
      query.otp = step;
    }
    const search = queryString.stringify(query);
    return {
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      state: { ...(location.state || {}), otpDepth: depth },
    };
  }

  pushOtpStep(step) {
    const { history, location } = this.props;
    const depth = get(location, 'state.otpDepth', 0) + 1;
    history.push(this.otpLocation(step, depth));
  }

  replaceOtpStep(step) {
    const { history, location } = this.props;
    history.replace(this.otpLocation(step, get(location, 'state.otpDepth', 0)));
  }

  // Back buttons use real history when we pushed the step, so the browser's
  // Forward/Back stay consistent; a deep-linked step just swaps the URL.
  backOtpStep(fallback) {
    const { history, location } = this.props;
    if (get(location, 'state.otpDepth', 0) > 0) {
      history.goBack();
    } else {
      this.replaceOtpStep(fallback);
    }
  }

  toggleShow() {
    const { showPassword } = this.state;

    this.setState({ showPassword: !showPassword });
  }

  render() {
    const {
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      loginError,
      scrollingDisabled,
      signupError,
      submitLogin,
      submitSignup,
      updateUserData,
      tab,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onResendVerificationEmail,
      onManageDisableScrolling,
      confirmError,
      submitSignupWithIdp,
    } = this.props;

    const isConfirm = tab === 'confirm';
    const {
      showPassword,
      tosModalOpen,
      authInfo,
      showTabs,
      review,
      isFromSignup,
      otpStep,
    } = this.state;
    // Once the one-time code button is clicked, show only the OTP step.
    const otpFocused = otpStep !== 'start';
    const otpUrlStep = otpStepFromLocation(location);
    // The name step has no URL of its own: a reload there opens the email step.
    const otpRequestedStep = otpUrlStep === 'profile' ? 'email' : otpUrlStep || 'start';

    const isLogin = tab === 'login';
    const locationFrom = get(location, 'state.from', null);
    const locationSearch = get(location, 'search', null);
    const parsedLocationSearch = queryString.parse(locationSearch);
    const locationSearchFrom = safeReturnPath(get(parsedLocationSearch, 'from', null));
    const authinfoFrom = get(authInfo, 'from', null);
    const signupContact = get(location, 'state.signupContact', null);
    const signupBook = get(location, 'state.signupBook', null);

    let from = null;
    if (locationFrom) {
      from = locationFrom;
    }
    if (authinfoFrom) {
      from = authinfoFrom;
    }

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!user.id;
    const userIsGuest = user?.attributes?.profile?.protectedData?.role?.includes('sportsman');

    const showGuestForm =
      userIsGuest &&
      (!user?.attributes?.profile?.protectedData?.preferredHuntingStates?.length > 0 ||
        !user?.attributes?.profile?.protectedData?.preferredHuntingSpecies?.length > 0);

    // We only want to show the email verification dialog in the signup
    // tab if the user isn't being redirected somewhere else
    // (i.e. `from` is present). We must also check the `emailVerified`
    // flag only when the current user is fully loaded.
    const showEmailVerification =
      !isLogin && currentUserLoaded && !user.attributes.emailVerified && !showGuestForm;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated) {
      if (from) {
        return <Redirect to={from} />;
      }

      // Hard reload, this is used from the NEW UI to redirect user to the correct page
      if (locationSearchFrom) {
        window.location.href = locationSearchFrom;
      }
    }

    const isLandowner = user?.attributes?.profile?.protectedData?.role?.includes('landowner');

    if (!isFromSignup) {
      if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
        // If landowner, redirect to landowner dashboard page
        if (isLandowner && !config.dev) {
          return <NamedRedirect name="LandownersPage" />;
        }

        return <NamedRedirect name="LandingPage" />;
      }
    }

    const loginErrorMessage = (
      <div className={css.error} data-testid="login-form-error">
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      </div>
    );

    const signupErrorMessage = (
      <div className={css.error} data-testid="signup-form-error">
        {isSignupEmailTakenError(signupError) ? (
          <FormattedMessage
            id="AuthenticationPage.signupFailedEmailAlreadyTaken"
            values={{
              link: (
                <Link to="/login" style={{ textDecoration: 'underline' }} className={css.error}>
                  logging in
                </Link>
              ),
            }}
          />
        ) : isCommonPasswordValidationError(signupError) ? (
          <FormattedMessage id="PasswordPolicy.passwordTooCommon" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    );

    const confirmErrorMessage = confirmError ? (
      <div className={css.error}>
        {isSignupEmailTakenError(confirmError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    ) : null;

    // eslint-disable-next-line no-confusing-arrow
    const errorMessage = (error, message) => (error ? message : null);
    const loginOrSignupError = isLogin
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

    const fromState = { state: from ? { from } : null };

    const tabs = [
      {
        text: (
          <h4 className={css.tabContent}>
            <FormattedMessage id="AuthenticationPage.signupLinkText" />
          </h4>
        ),
        selected: !isLogin,
        linkProps: {
          name: 'SignupPage',
          to: fromState,
        },
      },
      {
        text: (
          <h4 className={css.tabContent}>
            <FormattedMessage id="AuthenticationPage.loginLinkText" />
          </h4>
        ),
        selected: isLogin,
        linkProps: {
          name: 'LoginPage',
          to: fromState,
        },
      },
    ];

    const handleSubmitSignup = values => {
      const { fname, lname, ...rest } = values;
      const params = { firstName: fname?.trim(), lastName: lname?.trim(), ...rest };
      submitSignup(params);
    };

    const getSubmissionValues = values => {
      return {
        publicData: {
          usState:
            values.usState && values.usState.selectedPlace
              ? {
                  search: values.usState.search,
                  selectedPlace: {
                    address: values.usState.selectedPlace.address,
                    bounds: values.usState.selectedPlace.bounds,
                    origin: {
                      lat: values.usState.selectedPlace.origin.lat,
                      lng: values.usState.selectedPlace.origin.lng,
                    },
                    usCounty: values.usState.selectedPlace.usCounty,
                    usState: values.usState.selectedPlace.usState,
                  },
                }
              : null,
          interestedActivities: values.interestedActivities || [],
          preferredHuntingSpecies: values.preferredHuntingSpecies || [],
          preferredHuntingStates: Array.isArray(values.preferredHuntingStates)
            ? values.preferredHuntingStates.map(state => {
                return { address: state.address };
              })
            : [],
        },
        protectedData: {
          referralMethod: values.referralMethod || [],
        },
      };
    };

    const handleUpdateAccount = async data => {
      try {
        const updateParams = getSubmissionValues(data);
        await updateUserData(updateParams);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating account:', error);
      }
    };

    const handleSubmitConfirm = values => {
      const {
        idpToken,
        email,
        firstName,
        lastName,
        idpId,
        // Attribution params forwarded from OAuth state
        utm_source,
        utm_medium,
        utm_campaign,
        gclid,
        fbclid,
        ap,
        aid,
      } = authInfo;
      const {
        email: newEmail,
        firstName: newFirstName,
        lastName: newLastName,
        preferredHuntingStates = [],
        preferredHuntingSpecies = [],
        interestedActivities = [],
        usState,
        ...rest
      } = values;
      // Pass email, fistName or lastName to Flex API only if user has edited them
      // and they can't be fetched directly from idp provider (e.g. Facebook)

      const authParams = {
        ...(newEmail !== email && { email: newEmail }),
        ...(newFirstName !== firstName && { firstName: newFirstName }),
        ...(newLastName !== lastName && { lastName: newLastName }),
        usState,
        preferredHuntingStates,
        preferredHuntingSpecies,
        interestedActivities,
      };

      // If the confirm form has any additional values, pass them forward as user's protected data
      const protectedData = !isEmpty(rest) ? { ...rest } : null;

      // Add formatted phone number for email purposes
      if (protectedData && protectedData.phoneNumber) {
        const phoneNumberFormatted = format(protectedData.phoneNumber);

        protectedData.phoneNumberFormatted = phoneNumberFormatted;
      }

      submitSignupWithIdp({
        idpToken,
        idpId,
        ...authParams,
        ...(!!protectedData && { protectedData }),
        // Forward attribution params from OAuth state to signup
        ...(utm_source && { utm_source }),
        ...(utm_medium && { utm_medium }),
        ...(utm_campaign && { utm_campaign }),
        ...(gclid && { gclid }),
        ...(fbclid && { fbclid }),
        ...(ap && { ap }),
        ...(aid && { aid }),
      });
    };

    const getDefaultRoutes = () => {
      const routes = routeConfiguration();
      const baseUrl = apiBaseUrl();

      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      const fromParam = from
        ? `from=${from}`
        : locationSearchFrom
        ? `from=${encodeURIComponent(locationSearchFrom)}`
        : '';

      // Default route where user is returned after successfull authentication
      const defaultReturn = pathByRouteName('LandingPage', routes);
      const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

      // Route for confirming user data before creating a new user
      const defaultConfirm = pathByRouteName('ConfirmPage', routes);
      const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

      return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
    };
    // Build the attribution query string (utm_source, utm_medium, utm_campaign,
    // gclid, ap) to forward through the OAuth redirect.
    //
    // IMPORTANT: this reads from the `affiliate` module (the `aff_lt` cookie),
    // not from `window.location.search`. Attribution params are normally only
    // present in the URL on the original landing page (e.g. a paid-search ad
    // pointing at the homepage or a listing page); by the time a user has
    // browsed the site and reached this signup page via client-side routing,
    // `window.location.search` will almost never still contain them. The
    // `affiliate` module is the durable, cross-page store for this data - it is
    // populated once on app load (see `affiliate.loadParams()` in `app.js`) and
    // persisted in the `aff_lt` cookie, and `affiliate.get()`/`all()` fall back
    // to the current URL's query params automatically when no cookie exists yet
    // (e.g. a paid-search ad linking directly to `/signup?utm_source=...`).
    // Forwarding it through the OAuth `state` guards against the cookie
    // otherwise having to survive the full OAuth redirect round trip on its own.
    const buildAttributionQueryParams = () => {
      const attribution = affiliate.all();

      return Object.entries(attribution)
        .filter(([, value]) => !!value)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
    };

    const authWithFacebook = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;

      const attributionParams = buildAttributionQueryParams();
      const separator = attributionParams ? '&' : '';
      window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}${separator}${attributionParams}`;
    };

    const authWithGoogle = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;

      const attributionParams = buildAttributionQueryParams();
      const separator = attributionParams ? '&' : '';
      window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}${separator}${attributionParams}`;
    };

    const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

    // Form for confirming information from IdP (e.g. Facebook)
    // before new user is created to Flex
    const idpSignupForm = (
      <div className={css.content}>
        <IdpSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          isFromSignup
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          authInfo={authInfo}
          idp={idp}
          confirmErrorMessage={confirmErrorMessage}
        />
      </div>
    );

    // Social login buttons
    const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
    const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const showSocialLogins = showFacebookLogin || showGoogleLogin;

    const facebookButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
    );

    const googleButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
    );
    const socialLoginButtonsMaybe =
      showSocialLogins && showTabs ? (
        <div className={css.idpButtons}>
          {showFacebookLogin ? (
            <div data-testId="login-facebook-button" className={css.socialButtonWrapper}>
              <FacebookLoginButton onClick={() => authWithFacebook()}>
                <span className={css.buttonIcon}>{FacebookLogo}</span>
                {facebookButtonText}
              </FacebookLoginButton>
            </div>
          ) : null}

          {showGoogleLogin ? (
            <div className={css.socialButtonWrapper}>
              <GoogleLoginButton onClick={() => authWithGoogle()}>
                <span className={css.buttonIcon}>{GoogleLogo}</span>
                {googleButtonText}
              </GoogleLoginButton>
            </div>
          ) : null}
          <div className={css.socialButtonsOr}>
            <span className={css.socialButtonsOrText}>
              {isLogin ? (
                <FormattedMessage id="AuthenticationPage.orLogin" />
              ) : (
                <FormattedMessage id="AuthenticationPage.orSignup" />
              )}
            </span>
          </div>
        </div>
      ) : null;

    // sign-up headear selector depending on where user comes from in the app
    const signupHeaderPicker = () => {
      let signupHeaderMaybe = 'AuthenticationPage.signupDefault';
      if (signupContact) {
        signupHeaderMaybe = 'AuthenticationPage.signupContact';
      }
      if (signupBook) {
        signupHeaderMaybe = 'AuthenticationPage.signupBook';
      }
      return signupHeaderMaybe;
    };

    // Tabs for SignupForm and LoginForm
    const authenticationForms = (
      <div data-testid="authentication-form" className={css.content}>
        {showTabs && !otpFocused && (
          <>
            <div className={css.headerWrapper}>
              {isLogin ? (
                <FormattedHTMLMessage id="AuthenticationPage.loginDefault" />
              ) : (
                <FormattedHTMLMessage id={signupHeaderPicker()} />
              )}
            </div>

            <LinkTabNavHorizontal
              className={css.tabs}
              tabRootClassName={css.tab}
              tabClassName={css.tab}
              tabs={tabs}
            />
          </>
        )}

        {otpFocused ? null : loginOrSignupError}
        {otpFocused ? null : socialLoginButtonsMaybe}
        {otpFocused ? null : isLogin ? (
          <LoginForm
            className={css.form}
            onSubmit={submitLogin}
            inProgress={authInProgress}
            showPassword={showPassword}
            toggleShow={this.toggleShow}
          />
        ) : (
          <SignupForm
            className={css.form}
            user={user}
            isFromSignup
            from={from}
            onSubmit={handleSubmitSignup}
            onUpdate={handleUpdateAccount}
            inProgress={authInProgress}
            onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
            showPassword={showPassword}
            toggleShow={this.toggleShow}
            onHideTabs={() => this.setState({ showTabs: false })}
            isAuthenticated={isAuthenticated}
            currentUserLoaded={currentUserLoaded}
            showEmailVerification={showEmailVerification}
            isLandowner={isLandowner}
            config={config}
          />
        )}
        {/* OTP is on for every viewport, Log In tab only (hidden on Sign up). */}
        {showTabs && isLogin ? (
          <>
            {otpFocused ? null : (
              <div className={css.socialButtonsOr}>
                <span className={css.socialButtonsOrText}>
                  <FormattedMessage id="AuthenticationPage.orLoginWithCode" />
                </span>
              </div>
            )}
            <div className={css.magicLinkWrapper}>
              <OtpForm
                className={css.form}
                onStepChange={this.handleOtpStepChange}
                requestedStep={otpRequestedStep}
                onNavigate={step => this.pushOtpStep(step)}
                onNavigateBack={step => this.backOtpStep(step)}
                onSuccess={context => {
                  // Handle successful authentication
                  // If context has a 'from' path, redirect there
                  if (context?.from) {
                    window.location.href = context.from;
                  } else if (from) {
                    window.location.href = from;
                  } else if (locationSearchFrom) {
                    window.location.href = locationSearchFrom;
                  } else if (isLandowner && !config.dev) {
                    // Default redirect after successful auth
                    window.location.href = pathByRouteName('LandownersPage', routeConfiguration());
                  } else {
                    window.location.href = pathByRouteName('LandingPage', routeConfiguration());
                  }
                }}
                context={
                  from
                    ? {
                        from,
                        signupContact,
                        signupBook,
                      }
                    : null
                }
              />
            </div>
          </>
        ) : null}
      </div>
    );
    const formContent = isConfirm ? idpSignupForm : authenticationForms;
    const name = user?.attributes?.profile?.firstName;
    const email = <span className={css.email}>{user.attributes.email}</span>;

    const resendEmailLink = (
      <InlineTextButton rootClassName={css.modalHelperLink} onClick={onResendVerificationEmail}>
        <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
      </InlineTextButton>
    );
    const fixEmailLink = (
      <NamedLink className={css.modalHelperLink} name="ProfileSettingsBasePage">
        <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
      </NamedLink>
    );

    const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
      sendVerificationEmailError
    )
      ? 'AuthenticationPage.resendFailedTooManyRequests'
      : 'AuthenticationPage.resendFailed';
    const resendErrorMessage = sendVerificationEmailError ? (
      <p className={css.error}>
        <FormattedMessage id={resendErrorTranslationId} />
      </p>
    ) : null;

    const emailVerificationContent = (
      <div className={css.content}>
        <NamedLink className={css.verifyClose} name="ProfileSettingsBasePage">
          <span data-testId="verify-close-button" className={css.closeText}>
            <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
          </span>
          <IconClose rootClassName={css.closeIcon} />
        </NamedLink>
        <IconEmailSent className={css.modalIcon} />
        <h1 className={css.modalTitle}>
          <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
        </h1>
        <p className={css.modalMessage}>
          <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
        </p>
        {resendErrorMessage}

        <div className={css.bottomWrapper}>
          <p className={css.modalHelperText}>
            {sendVerificationEmailInProgress ? (
              <FormattedMessage id="AuthenticationPage.sendingEmail" />
            ) : (
              <FormattedMessage id="AuthenticationPage.resendEmail" values={{ resendEmailLink }} />
            )}
          </p>
          <p className={css.modalHelperText}>
            <FormattedMessage id="AuthenticationPage.fixEmail" values={{ fixEmailLink }} />
          </p>
        </div>
      </div>
    );

    const { siteTitle } = config;
    const schemaTitle = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });
    const schemaDescription = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { siteTitle });

    const topbar = (
      <div className={css.topbar}>
        <NamedLink className={css.home} name="LandingPage">
          <Logo
            className={css.logoMobile}
            title={intl.formatMessage({ id: 'AuthenticationPage.goToLandingPage' })}
            format="desktop"
          />
        </NamedLink>
      </div>
    );

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          name: schemaTitle,
          description: schemaDescription,
        }}
      >
        {topbar}
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.colLeft}>
            <div className={css.colLeftWrapper}>
              <NamedLink className={css.home} name="LandingPage">
                <Logo
                  className={css.logoDesktop}
                  alt={intl.formatMessage({ id: 'AuthenticationPage.goToLandingPage' })}
                  format="desktop"
                  variant="light"
                />
              </NamedLink>

              <div className={css.reviewWrapper}>
                <AuthenticationPageReview {...review} />
              </div>
            </div>
          </div>

          <div className={isConfirm ? css.colRightConfirm : css.colRight}>
            <div className={css.root}>
              {showEmailVerification ? emailVerificationContent : formContent}
            </div>
            <Modal
              id="AuthenticationPage.tos"
              isOpen={tosModalOpen}
              onClose={() => this.setState({ tosModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.termsWrapper}>
                <h2 className={css.termsHeading}>
                  <FormattedMessage id="AuthenticationPage.termsHeading" />
                </h2>
                <TermsOfService />
              </div>
            </Modal>
          </div>
        </LayoutWrapperMain>
      </Page>
    );
  }
}

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  reviewIndex: null,
};

const { bool, func, object, oneOf, shape, number } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  confirmError: propTypes.error,
  signupError: propTypes.error,
  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  updateUserData: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),
  reviewIndex: number,

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.Auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  signupStart: params => dispatch(signupStarted(params)),
  submitSignupWithIdp: params => dispatch(signupWithIdp(params)),
  submitSignup: params => dispatch(signup(params)),
  updateUserData: data => dispatch(updateProfile(data)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
